/**
 * aQuienProponer.js — a quién enseñar ya elegido al abrir «Enviar a firmas».
 *
 * Está aparte de la pantalla porque es una decisión con reglas, no una
 * presentación, y porque equivocarla manda el acta a quien no es. Es el mismo
 * motivo por el que existen `vistaDelDocumento.js` y `geometriaDeFirmas.js`.
 *
 * ── De dónde sale ────────────────────────────────────────────────────────
 *
 * «En el caso de firmas no persisten los correos del último envío y se tiene
 * que volver a configurar los destinatarios.»
 *
 * La pantalla solo proponía los ASISTENTES del acta, cruzados contra el
 * directorio por nombre. A quién se le había mandado de verdad la última vez no
 * se usaba para nada, así que cada vez que se abría la ventana había que
 * recomponer la lista entera.
 *
 * Y lo peor no era la molestia: un firmante de fuera —un correo que no está en
 * el directorio— no lo recupera NADA. Quien no se diera cuenta volvería a
 * mandar el acta sin esa persona.
 *
 * ── Las dos fuentes, y cuál manda ────────────────────────────────────────
 *
 * 1. El último envío. Es una decisión que alguien ya tomó para ESTA acta, así
 *    que gana sobre cualquier suposición.
 * 2. Los asistentes, si nunca se ha mandado. Eso es una propuesta.
 */

/** Deja un nombre comparable: sin acentos, sin dobles espacios, en minúsculas. */
export function llano(texto) {
  return String(texto || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/\s+/g, ' ').trim();
}

const enMinusculas = (correo) => String(correo || '').trim().toLowerCase();

/**
 * Reconstruye a los firmantes de una ronda como personas elegibles.
 *
 * ── La trampa ────────────────────────────────────────────────────────────
 *
 * El `id` que trae un firmante de una ronda es su **SignerId**, no el que tiene
 * en el directorio. Reutilizarlo tal cual haría que la pantalla diera por
 * conocida a todo el mundo, y un correo de fuera se enseñaría como si fuera de
 * la organización —sin su distintivo, y sin avisar de lo que es—.
 *
 * Así que a cada uno se le busca por su CORREO, que es lo único que significa
 * lo mismo en las dos listas. Quien no aparezca en el directorio es de fuera, y
 * se marca como tal.
 */
export function recuperarDeLaRonda(firmantes, directorio = []) {
  const porCorreo = new Map(directorio.map(p => [enMinusculas(p.correo), p]));

  return [...(firmantes || [])]
    .sort((a, b) => (a.orden || 0) - (b.orden || 0))
    .map(f => {
      const correo = enMinusculas(f && f.correo);
      if (!correo) return null;
      const enDirectorio = porCorreo.get(correo);
      if (enDirectorio) {
        return { ...enDirectorio, puesto: enDirectorio.puesto || f.puesto || null };
      }
      return {
        nombre: f.nombre || f.correo,
        correo: f.correo,
        puesto: f.puesto || null,
        aMano: true
      };
    })
    .filter(Boolean);
}

/** Los asistentes del acta que se encuentran en el directorio, por nombre. */
export function recuperarDeLosAsistentes(sugeridos = [], directorio = []) {
  const porNombre = new Map(directorio.map(p => [llano(p.nombre), p]));
  return (sugeridos || [])
    .map(n => porNombre.get(llano(n)))
    .filter(p => p && p.correo);
}

/** El mismo correo una sola vez: en una ronda en orden, un repetido se espera a sí mismo. */
export function sinRepetir(gente) {
  const vistos = new Set();
  return (gente || []).filter(p => {
    const c = enMinusculas(p && p.correo);
    if (!c || vistos.has(c)) return false;
    vistos.add(c);
    return true;
  });
}

/**
 * A quién proponer, y con qué orden de firma.
 *
 * @param {{ultimoEnvio?:Object, directorio?:Array, sugeridos?:Array}} de
 * @returns {{firmantes:Array, modo:string|null, origen:'envio'|'asistentes'|'nadie'}}
 *   `origen` dice de dónde salió, que es lo que permite probarlo y, si hiciera
 *   falta, decirlo en la pantalla.
 */
export function aQuienProponer({ ultimoEnvio = null, directorio = [], sugeridos = [] } = {}) {
  const deLaRonda = ultimoEnvio && Array.isArray(ultimoEnvio.firmantes)
    ? sinRepetir(recuperarDeLaRonda(ultimoEnvio.firmantes, directorio))
    : [];

  if (deLaRonda.length > 0) {
    const modo = (ultimoEnvio.modo === 'paralelo' || ultimoEnvio.modo === 'secuencial')
      ? ultimoEnvio.modo
      : null;
    return { firmantes: deLaRonda, modo, origen: 'envio' };
  }

  const deAsistentes = sinRepetir(recuperarDeLosAsistentes(sugeridos, directorio));
  if (deAsistentes.length > 0) {
    return { firmantes: deAsistentes, modo: null, origen: 'asistentes' };
  }

  return { firmantes: [], modo: null, origen: 'nadie' };
}

const proponer = {
  aQuienProponer, recuperarDeLaRonda, recuperarDeLosAsistentes, sinRepetir, llano
};
export default proponer;
